import type { AdapterConfig, ShellToRemoteMessage } from './types';

type PendingRequest = {
  resolve: (payload: unknown) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
};

export interface RequestResponseRegistryOptions {
  timeoutMs: number;
  timeoutError: (requestType: string) => Error;
}

export interface RequestRegistration<TResponsePayload> {
  requestId: string;
  requestType: string;
  resolve: (payload: TResponsePayload) => void;
  reject: (error: Error) => void;
}

export interface RequestResponseRegistry {
  register<TResponsePayload>(
    registration: RequestRegistration<TResponsePayload>,
  ): void;
  resolve(requestId: string, payload: unknown): boolean;
  rejectAll(errorFactory: () => Error): void;
}

export interface LifecycleGuard {
  readonly isDestroyed: boolean;
  assertActive(): void;
  destroy(teardown: () => void): void;
}

export interface ResettableDeferred<TValue> {
  readonly promise: Promise<TValue>;
  resolve(value: TValue): void;
  reject(reason?: unknown): void;
  reset(): void;
}

export type HandshakeDecision =
  | { type: 'ack'; selectedVersion: number }
  | { type: 'reject'; shellVersion: number; minSupported: number };

export type HandshakeResponse =
  | {
      type: 'ack';
      selectedVersion: number;
      platform?: AdapterConfig['platform'];
      extensionVersion?: string;
    }
  | { type: 'reject'; shellVersion: number; minSupported: number }
  | null;

export function createLifecycleGuard(entityName: string): LifecycleGuard {
  let destroyed = false;

  return {
    get isDestroyed(): boolean {
      return destroyed;
    },
    assertActive(): void {
      if (destroyed) {
        throw new Error(`${entityName} has already been destroyed`);
      }
    },
    destroy(teardown: () => void): void {
      if (destroyed) {
        return;
      }

      destroyed = true;
      teardown();
    },
  };
}

function createDeferred<TValue>(): {
  promise: Promise<TValue>;
  resolve: (value: TValue) => void;
  reject: (reason?: unknown) => void;
} {
  let resolve!: (value: TValue) => void;
  let reject!: (reason?: unknown) => void;
  const promise = new Promise<TValue>((nextResolve, nextReject) => {
    resolve = nextResolve;
    reject = nextReject;
  });

  return { promise, resolve, reject };
}

export function createResettableDeferred<TValue>(): ResettableDeferred<TValue> {
  let current = createDeferred<TValue>();

  return {
    get promise(): Promise<TValue> {
      return current.promise;
    },
    resolve(value: TValue): void {
      current.resolve(value);
    },
    reject(reason?: unknown): void {
      current.reject(reason);
    },
    reset(): void {
      current = createDeferred<TValue>();
    },
  };
}

export function createRequestResponseRegistry({
  timeoutMs,
  timeoutError,
}: RequestResponseRegistryOptions): RequestResponseRegistry {
  const pendingRequests = new Map<string, PendingRequest>();

  const register = <TResponsePayload>({
    requestId,
    requestType,
    resolve,
    reject,
  }: RequestRegistration<TResponsePayload>): void => {
    const timeoutId = setTimeout(() => {
      pendingRequests.delete(requestId);
      reject(timeoutError(requestType));
    }, timeoutMs);

    pendingRequests.set(requestId, {
      resolve: (responsePayload) => resolve(responsePayload as TResponsePayload),
      reject,
      timeoutId,
    });
  };

  const resolve = (requestId: string, payload: unknown): boolean => {
    const pending = pendingRequests.get(requestId);
    if (!pending) {
      return false;
    }

    pendingRequests.delete(requestId);
    clearTimeout(pending.timeoutId);
    pending.resolve(payload);
    return true;
  };

  const rejectAll = (errorFactory: () => Error): void => {
    for (const [requestId, pending] of pendingRequests) {
      clearTimeout(pending.timeoutId);
      pending.reject(errorFactory());
      pendingRequests.delete(requestId);
    }
  };

  return {
    register,
    resolve,
    rejectAll,
  };
}

export function isContractMessageFromSource<TSource extends string>(
  data: unknown,
  source: TSource,
): data is {
  source: TSource;
  type: string;
  version: number;
  payload: unknown;
  requestId?: string;
} {
  return (
    typeof data === 'object' &&
    data !== null &&
    'source' in data &&
    (data as { source: string }).source === source &&
    'type' in data &&
    typeof (data as { type: unknown }).type === 'string' &&
    'version' in data &&
    typeof (data as { version: unknown }).version === 'number' &&
    'payload' in data
  );
}

export function chooseHighestSharedVersion(
  localSupportedVersions: number[],
  remoteSupportedVersions: number[],
): number | null {
  const remoteVersions = new Set(remoteSupportedVersions);
  const sharedVersions = localSupportedVersions.filter((version) =>
    remoteVersions.has(version),
  );

  if (sharedVersions.length === 0) {
    return null;
  }

  return Math.max(...sharedVersions);
}

export function negotiateHandshake(
  shellSupportedVersions: number[],
  remoteSupportedVersions: number[],
): HandshakeDecision {
  const selectedVersion = chooseHighestSharedVersion(
    shellSupportedVersions,
    remoteSupportedVersions,
  );

  if (selectedVersion !== null) {
    return { type: 'ack', selectedVersion };
  }

  return {
    type: 'reject',
    shellVersion: Math.max(...shellSupportedVersions),
    minSupported: Math.min(...shellSupportedVersions),
  };
}

export function getHandshakeResponse(
  message: ShellToRemoteMessage,
): HandshakeResponse {
  if (message.type === 'HANDSHAKE_ACK') {
    return {
      type: 'ack',
      selectedVersion: message.payload.selectedVersion,
      platform: message.payload.platform,
      extensionVersion: message.payload.extensionVersion,
    };
  }

  if (message.type === 'HANDSHAKE_REJECT') {
    return {
      type: 'reject',
      shellVersion: message.payload.shellVersion,
      minSupported: message.payload.minSupported,
    };
  }

  return null;
}
